const INTEGER_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
})

/**
 * Format count-style metrics with grouped digits for dashboard readability.
 *
 * Problem: WebInternal dashboards were mixing raw numbers and ad hoc
 * `toLocaleString()` calls, so KPI cards and tables rendered counts
 * inconsistently.
 *
 * @param {number|string|null|undefined} value - raw count value
 * @param {string} [fallback='0'] - label to show when the value is missing
 * @returns {string} grouped integer label
 */
export function formatInteger(value, fallback = '0') {
  if (value == null) return fallback
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return fallback
  return INTEGER_FORMATTER.format(numericValue)
}

/**
 * Format decimal metrics with stable precision for KPI cards.
 *
 * Problem: averages and derived rates were mixing `toFixed()` calls and raw
 * values, which made similar metrics look unrelated across dashboards.
 *
 * @param {number|string|null|undefined} value - raw decimal value
 * @param {Object} [options] - precision and fallback options
 * @param {number} [options.minimumFractionDigits=0] - minimum decimal places
 * @param {number} [options.maximumFractionDigits=1] - maximum decimal places
 * @param {string} [options.fallback='—'] - label to show when the value is missing
 * @returns {string} formatted decimal label
 */
export function formatDecimal(
  value,
  {
    minimumFractionDigits = 0,
    maximumFractionDigits = 1,
    fallback = '—',
  } = {},
) {
  if (value == null) return fallback
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return fallback

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(numericValue)
}

/**
 * Format percentage metrics with shared precision and optional sign display.
 *
 * Problem: percent labels across internal dashboards used different decimal
 * places and sign rules, making adjacent KPI cards feel inconsistent.
 *
 * @param {number|string|null|undefined} value - raw percent value, already on a 0-100 scale
 * @param {Object} [options] - precision, sign, and fallback options
 * @param {number} [options.minimumFractionDigits=0] - minimum decimal places
 * @param {number} [options.maximumFractionDigits=1] - maximum decimal places
 * @param {'auto'|'always'|'exceptZero'|'never'} [options.signDisplay='auto'] - sign behavior
 * @param {string} [options.fallback='—'] - label to show when the value is missing
 * @returns {string} formatted percent label
 */
export function formatPercent(
  value,
  {
    minimumFractionDigits = 0,
    maximumFractionDigits = 1,
    signDisplay = 'auto',
    fallback = '—',
  } = {},
) {
  if (value == null) return fallback
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return fallback

  return `${new Intl.NumberFormat('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
    signDisplay,
  }).format(numericValue)}%`
}
